// VendorProfile.js (updated)
import React, {useState, useEffect, useRef} from "react";
import {
	Dialog, DialogContent, AppBar, Toolbar, IconButton, Typography, TextField, Button, Card, CardContent,
	Avatar, Box, Divider, Stack, Tabs, Tab, Snackbar, Alert, Backdrop, CircularProgress, FormControl, Select,
	MenuItem, InputLabel, Chip, useTheme, useMediaQuery, DialogActions
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import countries from "../SignUp/countries";
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
import { config } from "../../config";
import { refreshStoreInfo } from "../../utils/storeInfo";

/* --------------------------- Reusable Inputs --------------------------- */
const EditableTextField = ({
	                           label,
	                           value,
	                           section,
	                           field,
	                           onChange,
	                           isEditing,
	                           type = "text",
	                           placeholder = "",
	                           required = false,
	                           sx = {},
	                           InputProps = {},
                           }) => (
	<TextField
		label={label}
		variant="outlined"
		fullWidth
		required={required}
		disabled={!isEditing}
		value={value || ""}
		onChange={(e) => onChange(e, section, field)}
		type={type}
		placeholder={placeholder}
		sx={{
			"& .MuiOutlinedInput-root.Mui-disabled": {
				backgroundColor: "transparent !important",
				"& .MuiOutlinedInput-notchedOutline": {borderColor: "#dbdbdb"},
			},
			"& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#444 !important"},
			"& .MuiInputLabel-root.Mui-disabled": {color: "#444"},
			...sx,
		}}
		InputProps={InputProps}
	/>
);

const EditableSelect = ({
	                        label,
	                        value,
	                        section,
	                        field,
	                        options = [],
	                        onChange,
	                        isEditing,
	                        sx = {},
	                        showFlags = false,
	                        renderValue,
                        }) => {
	const defaultMenuProps = {PaperProps: {sx: {maxHeight: 350, minWidth: 220}}};

	const renderCountryItem = (opt) => (
		<Box sx={{display: "flex", alignItems: "center", gap: 1}}>
			<img
				loading="lazy"
				width="20"
				src={`https://flagcdn.com/w20/${opt.code.toLowerCase()}.png`}
				srcSet={`https://flagcdn.com/w40/${opt.code.toLowerCase()}.png 2x`}
				alt={opt.code}
			/>
			<Typography variant="body2">
				{opt.label} {opt.phone ? `(+${opt.phone})` : ""}
			</Typography>
		</Box>
	);

	return (
		<FormControl sx={{width: label === "Code" ? 120 : "100%"}}>
			<InputLabel>{label}</InputLabel>
			<Select
				label={label}
				variant="outlined"
				disabled={!isEditing}
				value={value || ""}
				onChange={(e) => onChange(e, section, field)}
				MenuProps={defaultMenuProps}
				renderValue={renderValue}
				sx={{
					"&.Mui-disabled .MuiOutlinedInput-notchedOutline": {borderColor: "#dbdbdb"},
					"&.Mui-disabled": {
						backgroundColor: "transparent !important",
						color: "#444 !important",
					},
					"& .MuiInputLabel-root.Mui-disabled": {color: "#444"},
					"& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#444 !important"},
					...sx,
				}}
			>
				{options.map((opt) => (
					<MenuItem key={opt.code || opt.label} value={opt.value}>
						{showFlags ? renderCountryItem(opt) : opt.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

/* ------------------------------- API BASE ------------------------------ */
// Use the StoreManager service (as per your new APIs)
const STOREMANAGER_BASE = config.API_MSWSSTOREMANAGER_BASE_URL;
const ONBOARDING_BASE = `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores`;

const readFromStorage = (key) =>
	(typeof window !== "undefined" && (sessionStorage.getItem(key) ?? localStorage.getItem(key))) || null;

const resolveAuth = () => {
	const vendorIdKeys = ["vendorId", "vendorID", "vendor_id"];
	const tokenKeys = ["sessionToken", "X-Session-Token", "xSessionToken", "token"];
	let vendorId = null;
	let sessionToken = null;
	for (const k of vendorIdKeys) {
		vendorId = readFromStorage(k);
		if (vendorId) break;
	}
	for (const k of tokenKeys) {
		sessionToken = readFromStorage(k);
		if (sessionToken) break;
	}
	return {vendorId, sessionToken};
};

export default function VendorProfile({open, onClose}) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	/* ------------------------------- State -------------------------------- */
	const [errorMsg, setErrorMsg] = useState("");
	const [discardOpen, setDiscardOpen] = useState(false);

	const [loading, setLoading] = useState(false);
	const [updateLoading, setUpdateLoading] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [activeTab, setActiveTab] = useState(0);

	const [{vendorId, sessionToken}, setAuth] = useState({vendorId: null, sessionToken: null});

	const [profile, setProfile] = useState({
		vendorId: null,
		email: "",
		phoneCountryCode: "",
		phoneNumber: "",

		firstName: "",
		lastName: "",
		storeAddress: {
			line1: "",
			line2: "",
			city: "",
			state: "",
			zip: "",
			country: "",
		},

		business: {
			storeType: "",
			storeName: "",
			domainName: "",
			storeUrl: "",
			category: "",
			storeDescription: "",
		},
	});


	// Keep a snapshot to detect what changed
	const originalRef = useRef(null);

	/* ---------------------------- Derived Data ---------------------------- */
	const phoneCodeOptions = countries
		.filter((c) => c.phone)
		.map((c) => ({
			code: c.code,
			label: c.label,
			phone: c.phone,
			value: `+${c.phone}`,
		}));

	const storeTypeOptions = [
		{label: "Retail", value: "Retail"},
		{label: "Wholesale", value: "Wholesale"},
	];

	const handleTabChange = (_e, v) => setActiveTab(v);
	const handleEdit = () => setIsEditing(true);
	const handleCancel = () => {
		// Revert to original snapshot
		if (originalRef.current) setProfile(structuredClone(originalRef.current));
		setIsEditing(false);
	};

	const handleChange = (event, section, field) => {
		const {value} = event.target;
		if (section) {
			setProfile((prev) => ({
				...prev,
				[section]: {
					...prev[section],
					[field]: value,
				},
			}));
		} else {
			setProfile((prev) => ({...prev, [field]: value}));
		}
	};

	const handleSave = async () => {
		const ok = await updateVendorProfile();
		if (ok) setIsEditing(false);
	};

	const isDirty = () =>
		Boolean(originalRef.current) &&
		JSON.stringify(profile) !== JSON.stringify(originalRef.current);

	const handleClose = () => {
		if (updateLoading) return;
		if (isEditing && isDirty()) {
			setDiscardOpen(true);
			return;
		}
		setIsEditing(false);
		onClose();
	};

	const confirmDiscard = () => {
		setDiscardOpen(false);
		handleCancel();
		onClose();
	};

	useEffect(() => {
		if (open) return;
		setIsEditing(false);
		setDiscardOpen(false);
		if (originalRef.current) setProfile(structuredClone(originalRef.current));
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const auth = resolveAuth();
		setAuth(auth);
	}, [open]);

	useEffect(() => {
		if (!open || !vendorId || !sessionToken) {
			if (open) setErrorMsg("Missing vendorId/sessionToken in storage.");
			return;
		}
		(async () => {
			await getVendorInfo();
			await getVendorProfile();
		})();
	}, [open, vendorId, sessionToken]);

	/* ------------------------------- Helpers ------------------------------ */
	const findByCode = (code) => countries.find((c) => c.code === code);

	const normalizePhone = (raw) => (raw || "").replace(/[^\d+]/g, "");


	const splitPhoneWithCountry = (fullPhone, countryCode) => {
		const clean = (fullPhone || "").replace(/[^\d+]/g, "");
		const c = countryCode ? findByCode(countryCode) : null;
		const codeString = c?.phone ? `+${c.phone}` : null;
		if (codeString && clean.startsWith(codeString.replace("+", ""))) {
			// In case API returned without '+'
			const onlyDigits = clean.replace(/\D/g, "");
			const national = onlyDigits.slice(String(c.phone).length);
			return {phoneCountryCode: `+${c.phone}`, phoneNumber: national};
		}
		if (codeString && clean.startsWith(codeString)) {
			const national = clean.slice(codeString.length);
			return {phoneCountryCode: codeString, phoneNumber: national};
		}
		// Fallback: try to infer by longest matching phone code
		const onlyDigits = clean.replace(/\D/g, "");
		const sorted = countries
			.filter((x) => x.phone)
			.sort((a, b) => String(b.phone).length - String(a.phone).length);
		for (const k of sorted) {
			const code = String(k.phone);
			if (onlyDigits.startsWith(code)) {
				return {phoneCountryCode: `+${k.phone}`, phoneNumber: onlyDigits.slice(code.length)};
			}
		}
		// If all else fails
		return {phoneCountryCode: profile.phoneCountryCode, phoneNumber: profile.phoneNumber};
	};

	const shallowEqual = (a, b) => a === b;

	const hasPersonalChanges = (curr, orig) => {
		if (!orig) return true;
		const nameChanged =
			curr.firstName !== orig.firstName ||
			curr.lastName !== orig.lastName;

		const addrKeys = ["line1", "line2", "city", "state", "zip", "country"];
		const addressChanged = addrKeys.some(
			(k) => (curr.storeAddress?.[k] || "") !== (orig.storeAddress?.[k] || "")
		);

		return nameChanged || addressChanged;
	};


	const hasBusinessChanges = (curr, orig) => {
		if (!orig) return true;
		const kb = ["storeType", "category", "storeDescription", "storeUrl"];
		return kb.some((k) => !shallowEqual(curr.business?.[k], orig.business?.[k]));
	};

	const normalizeStoreUrlAndDomain = (value) => {
		if (!value) return {storeUrl: "", domainName: ""};

		let urlStr = String(value).trim();
		const ensureUrl = (s) => (s.startsWith("http://") || s.startsWith("https://") ? s : `https://${s}`);

		// Try to treat plain subdomain as "subdomain.microstores.net"
		// If it looks like a bare token (letters/numbers/- only), assume it's the subdomain
		const bareToken = /^[a-z0-9-]+$/i;
		if (bareToken.test(urlStr)) {
			urlStr = `${urlStr}.microstores.net`;
		}

		// If still missing scheme, add https://
		urlStr = ensureUrl(urlStr);

		let domainName = "";
		try {
			const u = new URL(urlStr);
			const host = u.hostname || "";
			// If it's *.microstores.net → take the first label
			if (host.endsWith(".microstores.net")) {
				domainName = host.replace(".microstores.net", "");
			} else {
				// Otherwise take the first label (best effort)
				domainName = host.split(".")[0] || "";
			}
		} catch {
			// If URL parsing fails, fallback to token
			domainName = urlStr.replace(/^https?:\/\//, "").split(".")[0] || "";
		}

		return {storeUrl: urlStr, domainName};
	};

// Build a canonical store URL from a clean domainName (subdomain)
	const buildStoreUrlFromDomainName = (sub) => {
		const token = (sub || "").trim().replace(/^https?:\/\//, "").replace(/\.microstores\.net$/i, "");
		return token ? `https://${token}.microstores.net` : "";
	};

	/* ----------------------------- API: GET ------------------------------- */
	const getVendorInfo = async () => {
		if (!vendorId || !sessionToken) return;
		setLoading(true);
		setErrorMsg("");
		try {
			const res = await fetch(`${STOREMANAGER_BASE}/api/vendors/${vendorId}/info`, {
				method: "GET",
				headers: {"X-Session-Token": sessionToken},
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json?.message || "Failed to fetch vendor info");

			const vp = json.vendorProfile || {};
			const vb = json.vendorBusinessInfo || {};

			// Split phone by country code (you already have splitPhoneWithCountry)
			const {phoneCountryCode, phoneNumber} = splitPhoneWithCountry(vp.phoneNumber || "", vp.countryCode);

			// storefrontWeb -> storeUrl (+ domainName) with normalization
			const norm = normalizeStoreUrlAndDomain(vb.storefrontWeb || "");

			setProfile((prev) => ({
				...prev,
				email: vp.emailAddress ?? prev.email,
				phoneCountryCode: phoneCountryCode || prev.phoneCountryCode,
				phoneNumber: phoneNumber || prev.phoneNumber,
				storeAddress: {
					...prev.storeAddress,
					country: vp.countryName || prev.storeAddress.country,
				},
				business: {
					...prev.business,
					storeType: vb.storeType ?? prev.business.storeType,
					category: vb.businessCategory ?? prev.business.category,
					storeDescription: vb.storeDescription ?? prev.business.storeDescription,
					storeUrl: norm.storeUrl || prev.business.storeUrl,
					domainName: norm.domainName || prev.business.domainName,
				},
			}));

			// Don't snapshot here; we'll snapshot after onboarding API merges
		} catch (e) {
			setErrorMsg(e.message || "Unable to load profile");
		} finally {
			setLoading(false);
		}
	};

	const getVendorProfile = async () => {
		if (!vendorId || !sessionToken) return;
		setLoading(true);
		setErrorMsg("");
		try {
			const res = await fetch(`${ONBOARDING_BASE}/vendorProfile/${vendorId}`, {
				method: "GET",
				headers: {"X-Session-Token": sessionToken},
			});
			const json = await res.json();
			if (!res.ok || json.statusCode !== 200) throw new Error(json?.message || "Failed to fetch profile");

			const d = json.data || {};
			setProfile((prev) => {
				const next = {
					...prev,
					firstName: d.firstName ?? prev.firstName,
					lastName: d.lastName ?? prev.lastName,
					business: {
						...prev.business,
						// API has businessName ⇒ map to storeName
						storeName: d.businessName ?? prev.business.storeName,
					},
					storeAddress: {
						...prev.storeAddress,
						line1: d.addressLine1 ?? prev.storeAddress.line1,
						line2: d.addressLine2 ?? prev.storeAddress.line2,
						city: d.city ?? prev.storeAddress.city,
						state: d.state ?? prev.storeAddress.state,
						zip: d.postalCode ?? prev.storeAddress.zip,
						country: d.country ?? prev.storeAddress.country,
					},
				};
				// Snapshot the fully merged state immediately (no second setState needed)
				originalRef.current = structuredClone(next);
				return next;
			});
		} catch (e) {
			setErrorMsg(e.message || "Unable to load profile");
		} finally {
			setLoading(false);
		}
	};


	/* ---------------------------- API: PUT(s) ----------------------------- */
	const updateVendorProfile = async () => {
		if (!vendorId || !sessionToken) return false;

		const curr = profile;
		const orig = originalRef.current;

		if (!(curr.storeAddress?.line2 || "").trim()) {
			setErrorMsg("Address Line 2 is required");
			return false;
		}

		const needBusiness = hasBusinessChanges(curr, orig);
		const needPersonal = hasPersonalChanges(curr, orig);

		// If nothing changed, nothing to save
		if (!needBusiness && !needPersonal) return true;

		setUpdateLoading(true);
		setErrorMsg("");

		try {
			const reqs = [];

			/* ---------- Business (StoreManager) ---------- */
			if (needBusiness) {
				const norm = normalizeStoreUrlAndDomain(curr.business?.storeUrl || "");
				const businessPayload = {
					businessCategory: curr.business?.category || "",
					storeType: curr.business?.storeType || "",
					storeDescription: curr.business?.storeDescription || "",
					storefrontWeb: norm.storeUrl, // normalized https URL or empty
				};

				reqs.push(
					fetch(`${STOREMANAGER_BASE}/api/vendors/${vendorId}/business-info`, {
						method: "PUT",
						headers: {
							"Content-Type": "application/json;charset=UTF-8",
							"X-Session-Token": sessionToken,
						},
						body: JSON.stringify(businessPayload),
					}).then(async (r) => {
						const j = await r.json().catch(() => ({}));
						if (!r.ok) throw new Error(j?.message || "Failed to update business info");

						const back = normalizeStoreUrlAndDomain(j.storefrontWeb || "");
						setProfile((prev) => ({
							...prev,
							business: {
								...prev.business,
								category: j.businessCategory ?? prev.business.category,
								storeType: j.storeType ?? prev.business.storeType,
								storeDescription: j.storeDescription ?? prev.business.storeDescription,
								storeUrl: back.storeUrl || prev.business.storeUrl,
								domainName: back.domainName || prev.business.domainName,
							},
						}));
					})
				);
			}

			/* ---------- Personal (Onboarding) ---------- */
			if (needPersonal) {
				const personalPayload = {
					// REQUIRED by the service in the request body
					sessionToken,
					vendorId,
					// Full object expected by validateFields()
					businessName: curr.business?.storeName || "",
					firstName: curr.firstName || "",
					lastName: curr.lastName || "",

					addressLine1: curr.storeAddress?.line1 || "",
					addressLine2: curr.storeAddress?.line2 || "",
					city: curr.storeAddress?.city || "",
					state: curr.storeAddress?.state || "",
					postalCode: curr.storeAddress?.zip || "",
					country: curr.storeAddress?.country || "",
				};


				reqs.push(
					fetch(`${ONBOARDING_BASE}/vendorProfile/${vendorId}`, {
						method: "PUT",
						headers: {
							"Content-Type": "application/json;charset=UTF-8",
							// Header is fine to keep, but the service checks the body’s sessionToken
							"X-Session-Token": sessionToken,
						},
						body: JSON.stringify(personalPayload),
					}).then(async (r) => {
						const j = await r.json().catch(() => ({}));
						// On this service, success is HTTP 200 and j.statusCode === 200
						if (!r.ok || (j?.statusCode && j.statusCode !== 200)) {
							throw new Error(j?.message || "Failed to update personal info");
						}
						const d = j?.data || {};
						setProfile((prev) => ({
							...prev,
							firstName: d.firstName ?? prev.firstName,
							lastName: d.lastName ?? prev.lastName,
							storeAddress: {
								...prev.storeAddress,
								line1: d.addressLine1 ?? prev.storeAddress.line1,
								line2: d.addressLine2 ?? prev.storeAddress.line2,
								city: d.city ?? prev.storeAddress.city,
								state: d.state ?? prev.storeAddress.state,
								zip: d.postalCode ?? prev.storeAddress.zip,
								country: d.country ?? prev.storeAddress.country,
							},
						}));
					})
				);
			}

			const names = [
				needBusiness && "business",
				needPersonal && "personal",
			].filter(Boolean);
			const settled = await Promise.allSettled(reqs);
			const succeeded = names.filter((_, i) => settled[i].status === "fulfilled");
			const failures = settled.filter((r) => r.status === "rejected");

			// Only sections the server accepted become the new baseline.
			setProfile((prev) => {
				const base = structuredClone(originalRef.current || prev);
				if (succeeded.includes("business")) base.business = structuredClone(prev.business);
				if (succeeded.includes("personal")) {
					base.firstName = prev.firstName;
					base.lastName = prev.lastName;
					base.storeAddress = structuredClone(prev.storeAddress);
				}
				originalRef.current = base;
				return prev;
			});

			if (failures.length > 0) {
				setErrorMsg(failures[0].reason?.message || "Unable to update profile");
				return false;
			}
			setShowSuccess(true);
			if (succeeded.includes("personal")) refreshStoreInfo();
			return true;
		} catch (e) {
			setErrorMsg(e.message || "Unable to update profile");
			return false;
		} finally {
			setUpdateLoading(false);
		}
	};


	/* --------------------------------- UI --------------------------------- */
	return (
		<Dialog
			open={open}
			onClose={handleClose}
			scroll="paper"
			fullScreen={isMobile}
			maxWidth="md"
			fullWidth
			sx={{
				"& .MuiDialog-paper": {
					marginTop: isMobile ? "54px" : "90px",
					height: isMobile ? "calc(100% - 54px)" : "calc(100% - 90px)",
					borderRadius: isMobile ? 0 : 4,
					zIndex: 2000,
				},
			}}
		>
			<ThemeProvider theme={theme}>
				<AppBar position="relative" elevation={0} sx={{backgroundColor: "#ffffff", color: "#000000"}}>
					<Toolbar>
						<Typography sx={{flex: 1}} variant="h6">
							Vendor Profile
						</Typography>
						<Chip
							label="Active Vendor"
							size="small"
							sx={{
								mr: 2,
								display: isMobile ? "none" : "inline-flex",
								bgcolor: "#eaffd6",
								color: "#006400",
								fontWeight: 500,
								"& .MuiChip-label": {px: 1.5},
							}}
						/>
						<IconButton edge="end" onClick={handleClose}>
							<CloseIcon/>
						</IconButton>
					</Toolbar>
				</AppBar>

				{/* Busy & Alerts */}
				<Backdrop open={loading || updateLoading} sx={{zIndex: 9999, color: "#fff"}}>
					<CircularProgress color="inherit"/>
				</Backdrop>
				<Snackbar open={showSuccess} autoHideDuration={3000} onClose={() => setShowSuccess(false)}>
					<Alert severity="success" onClose={() => setShowSuccess(false)}>
						Profile Updated Successfully
					</Alert>
				</Snackbar>
				<Snackbar open={Boolean(errorMsg)} autoHideDuration={4000} onClose={() => setErrorMsg("")}>
					<Alert severity="error" onClose={() => setErrorMsg("")}>
						{errorMsg}
					</Alert>
				</Snackbar>

				<DialogContent dividers sx={{p: isMobile ? 2 : 4}}>
					<Card sx={{width: "100%", boxShadow: 0, backgroundColor: "transparent", overflow: "visible"}}>
						<CardContent sx={{p: 0}}>
							{/* Header: avatar / name */}
							<Box display="flex" flexDirection="column" alignItems="center" mb={3}>
								<Avatar
									sx={{width: 64, height: 64, backgroundColor: theme.palette.primary.main, color: "#000", fontSize: "1.5rem", mb: 1}}
								>
									{profile.firstName?.charAt(0)?.toUpperCase() || "V"}
								</Avatar>
								<Typography variant="h6" fontWeight="bold">
									{profile.firstName} {profile.lastName}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{profile.email}
								</Typography>
								{!isEditing && activeTab !== 0 && (
									<Box sx={{mt: 1}}>
										<Button
											variant="outlined" color="secondary"
											onClick={handleEdit}
											startIcon={<EditIcon/>}
											sx={{textTransform: "none", borderRadius: 1}}
										>
											Edit
										</Button>
									</Box>
								)}
								{isEditing && (
									<Box
										sx={{
											mt: 1,
											width: "100%",
											display: "flex",
											justifyContent: "flex-end",
											gap: 2,
										}}
									>
										<Button
											variant="outlined"
											color="error"
											startIcon={<CancelIcon/>}
											onClick={handleCancel}
											disabled={updateLoading}
											sx={{textTransform: "none", borderRadius: 1}}
										>
											Cancel
										</Button>
										<Button
											variant="contained"
											startIcon={updateLoading ? null : <SaveIcon/>}
											onClick={handleSave}
											disabled={updateLoading}
											sx={{textTransform: "none", borderRadius: 1}}
										>
											{updateLoading ? <CircularProgress size={24} sx={{color: "#fff"}}/> : "Save"}
										</Button>
									</Box>
								)}
							</Box>

							{/* Tabs */}
							<Box sx={{width: "100%", bgcolor: "#f5f5f5", borderRadius: 2, p: 0.5, mb: 3}}>
								<Tabs
									value={activeTab}
									onChange={handleTabChange}
									variant="fullWidth"
									TabIndicatorProps={{style: {display: "none"}}}
									sx={{
										minHeight: "auto",
										"& .MuiTab-root": {
											textTransform: "none",
											fontWeight: 500,
											borderRadius: "6px",
											color: "#666",
											minHeight: "auto",
											py: 1,
										},
										"& .Mui-selected": {
											bgcolor: "#ff9100",
											color: "#fff!important",
											boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
										},
									}}
								>
									<Tab label="Contact"/>
									<Tab label="Personal"/>
									<Tab label="Business"/>
								</Tabs>
							</Box>

							{/* Tab: Contact */}
							{activeTab === 0 && (
								<Box>
									<Typography variant="h6" fontWeight={600} mb={1}>
										Contact Details
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={2}>
										Email and phone used for vendor account communication
									</Typography>

									<Stack spacing={2} mb={2}>
										<EditableTextField
											label="Email"
											type="email"
											value={profile.email}
											field="email"
											onChange={handleChange}
											isEditing={false}
											sx={{"& .MuiOutlinedInput-root": {pr: 4}}}
											InputProps={{startAdornment: <MailIcon sx={{mr: 1}}/>}}
										/>
										<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
											<EditableSelect
												label="Code"
												value={profile.phoneCountryCode}
												field="phoneCountryCode"
												options={phoneCodeOptions}
												showFlags
												renderValue={(v) => v}
												onChange={handleChange}
												isEditing={false}
											/>
											<EditableTextField
												label="Phone Number"
												value={profile.phoneNumber}
												field="phoneNumber"
												onChange={handleChange}
												isEditing={false}
												sx={{flexGrow: 1}}
												InputProps={{startAdornment: <PhoneIcon sx={{mr: 1}}/>}}
											/>
										</Stack>
									</Stack>
								</Box>
							)}

							{/* Tab: Personal */}
							{activeTab === 1 && (
								<Box>
									<Typography variant="h6" fontWeight={600} mb={1}>
										Personal Information
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={2}>
										Details about you and your store’s physical address
									</Typography>
									<Stack spacing={2} mb={4}>
										<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
											<EditableTextField
												label="First Name"
												value={profile.firstName}
												field="firstName"
												onChange={handleChange}
												isEditing={isEditing}
											/>
											<EditableTextField
												label="Last Name"
												value={profile.lastName}
												field="lastName"
												onChange={handleChange}
												isEditing={isEditing}
											/>
										</Stack>
										<Divider/>
										<Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
											Store Address
										</Typography>
										<EditableTextField
											label="Address Line 1"
											value={profile.storeAddress.line1}
											section="storeAddress"
											field="line1"
											onChange={handleChange}
											isEditing={isEditing}
										/>
										<EditableTextField
											label="Address Line 2"
											value={profile.storeAddress.line2}
											section="storeAddress"
											field="line2"
											onChange={handleChange}
											isEditing={isEditing}
											required
										/>
										<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
											<EditableTextField
												label="City"
												value={profile.storeAddress.city}
												section="storeAddress"
												field="city"
												onChange={handleChange}
												isEditing={isEditing}
											/>
											<EditableTextField
												label="State"
												value={profile.storeAddress.state}
												section="storeAddress"
												field="state"
												onChange={handleChange}
												isEditing={isEditing}
											/>
											<EditableTextField
												label="Zip / PIN"
												value={profile.storeAddress.zip}
												section="storeAddress"
												field="zip"
												onChange={handleChange}
												isEditing={isEditing}
											/>
										</Stack>
										<EditableTextField
											label="Country"
											value={profile.storeAddress.country}
											section="storeAddress"
											field="country"
											onChange={handleChange}
											isEditing={isEditing} // Auth PUT /microstores/vendorProfile/{vendorId}
										/>
									</Stack>
								</Box>
							)}

							{/* Tab: Business */}
							{activeTab === 2 && (
								<Box>
									<Typography variant="h6" fontWeight={600} mb={1}>
										Business Information
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={2}>
										Store details shown to your customers
									</Typography>
									<Stack spacing={2} mb={4}>
										<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
											<EditableSelect
												label="Store Type"
												value={profile.business.storeType || ""}
												section="business"
												field="storeType"
												options={storeTypeOptions}
												onChange={handleChange}
												isEditing={isEditing}
											/>
											<EditableTextField
												label="Store Name"
												value={profile.business.storeName}
												section="business"
												field="storeName"
												onChange={handleChange}
												isEditing={false} // not part of new APIs; keeping view-only
											/>
										</Stack>
										<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
											<EditableTextField
												label="Domain Name"
												value={profile.business.domainName}
												onChange={(e) => {
													const nextDomain = e.target.value;
													setProfile((prev) => ({
														...prev,
														business: {
															...prev.business,
															domainName: nextDomain,
															storeUrl: buildStoreUrlFromDomainName(nextDomain),
														},
													}));
												}}
												isEditing={false}
											/>
											<TextField
												label="Store URL"
												value={profile.business.storeUrl}
												fullWidth
												disabled
												sx={{
													"& .MuiOutlinedInput-root.Mui-disabled": {
														backgroundColor: "transparent !important",
														"& .MuiOutlinedInput-notchedOutline": {borderColor: "#dbdbdb"},
													},
													"& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#444 !important"},
												}}
											/>
										</Stack>
										<EditableTextField
											label="Category"
											value={profile.business.category}
											section="business"
											field="category"
											onChange={handleChange}
											isEditing={isEditing} // editable; PUT /business-info
										/>
										<TextField
											label="Store Description"
											multiline
											minRows={4}
											inputProps={{maxLength: 500}}
											value={profile.business.storeDescription}
											onChange={(e) =>
												setProfile((p) => ({
													...p,
													business: {...p.business, storeDescription: e.target.value},
												}))
											}
											disabled={!isEditing}
											sx={{
												"& .MuiOutlinedInput-root.Mui-disabled": {
													backgroundColor: "transparent !important",
													"& .MuiOutlinedInput-notchedOutline": {borderColor: "#dbdbdb"},
												},
												"& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#444 !important"},
											}}
										/>
										<Typography variant="caption" color="text.secondary">
											{profile.business.storeDescription.length}/500 characters
										</Typography>
									</Stack>
								</Box>
							)}

						</CardContent>
					</Card>
				</DialogContent>

				<Dialog open={discardOpen} onClose={() => setDiscardOpen(false)}>
					<DialogContent sx={{p: 3}}>
						<Typography variant="h6" gutterBottom>
							Discard changes?
						</Typography>
						<Typography color="text.secondary" sx={{mb: 2}}>
							You have unsaved changes. Closing will discard them.
						</Typography>
						<DialogActions sx={{px: 0}}>
							<Button onClick={() => setDiscardOpen(false)} color="secondary">Keep editing</Button>
							<Button variant="contained" color="error" onClick={confirmDiscard}>
								Discard
							</Button>
						</DialogActions>
					</DialogContent>
				</Dialog>
			</ThemeProvider>
		</Dialog>
	);
}
